import {Container} from '@/components/ui/container/Container'
import {Divider} from '@/components/ui/divider/Divider'
import {useTheme} from 'styled-components'
import {
    StyledCloseBannerButton,
    StyledProPhotosHeader,
    StyledProPhotosLastUpdated,
    StyledProPhotosHeaderText
} from './style'
import {useTranslation} from '@/translations/i18n'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {useState, useEffect} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {SubHeaderBanner} from '@/features/photoset/components/subheader-banner/SubHeaderBanner'
import {Trans} from 'react-i18next'
import {formatLocaleDate} from '@/utilities/helpers'
import {Button} from '@/components/ui/button/Button'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {DialogStore} from '@ariakit/react'
import {ProPhotosConfirmSubmitModal} from '../pro-photos-confirm-submit-modal/ProPhotosConfirmSubmitModal'

interface ProPhotosHeaderProps {
    title: string
    description: string
    updatedAt?: string | null
    updatedBy?: number | null
    errorDescription: string
    isFieldsChanged: boolean
    onSubmit: () => void
    hasErrors: boolean
    isPending: boolean
    submitModalStore: DialogStore
}

export const ProPhotosHeader = ({
    title,
    description,
    updatedAt,
    updatedBy,
    errorDescription,
    isFieldsChanged,
    onSubmit,
    hasErrors,
    isPending,
    submitModalStore
}: ProPhotosHeaderProps) => {
    const {palette} = useTheme()
    const {t} = useTranslation()
    const [showBanner, setShowBanner] = useState(false)

    useEffect(() => {
        if (!hasErrors && showBanner) {
            setShowBanner(false)
        }
    }, [hasErrors, showBanner])

    return (
        <>
            <Container fullWidth fullHeight={false}>
                <StyledProPhotosHeader gap={2} justify="space-between" align="center">
                    <StyledProPhotosHeaderText gap={2} align="center">
                        <h1>{title}</h1>
                        <p>{description}</p>
                    </StyledProPhotosHeaderText>

                    <Flexbox gap={4} align="center">
                        {updatedAt && updatedBy && (
                            <StyledProPhotosLastUpdated>
                                <p>
                                    <Trans
                                        i18nKey="vetting:reordering:last_updated_by"
                                        components={{strong: <strong />}}
                                        values={{userId: updatedBy}}
                                    />
                                </p>
                                <span>{formatLocaleDate(updatedAt, 'lll')}</span>
                            </StyledProPhotosLastUpdated>
                        )}
                        <Tooltip
                            rootProps={{open: isFieldsChanged ? false : undefined}}
                            isOpenOnClickEnabled={false}
                            content={t('vetting:reordering:no_changes_to_save')}
                            triggerProps={{asChild: true}}
                        >
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={!isFieldsChanged}
                                onClick={() => (hasErrors ? setShowBanner(true) : submitModalStore.toggle())}
                            >
                                {t('vetting:reordering:update_listing')}
                            </Button>
                        </Tooltip>
                    </Flexbox>
                </StyledProPhotosHeader>
                <ProPhotosConfirmSubmitModal store={submitModalStore} onSubmit={onSubmit} isPending={isPending} />
            </Container>

            <Divider direction="horizontal" background={palette.neutral[300]} />

            {showBanner && (
                <SubHeaderBanner
                    title={t('vetting:reordering:error_title')}
                    subtitle={errorDescription}
                    type={'danger'}
                    endSlot={
                        <StyledCloseBannerButton
                            variant="tertiary"
                            size="sm"
                            iconOnly
                            onClick={() => setShowBanner(false)}
                        >
                            <XCloseIcon size={20} fill={palette.neutral['400']} />
                        </StyledCloseBannerButton>
                    }
                />
            )}
        </>
    )
}
ProPhotosHeader.displayName = 'ProPhotosHeader'
